import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Briefcase, GraduationCap, Award, 
  Code, Calendar, Clock, MapPin, ExternalLink, Tag
} from 'lucide-react';
import useResponsive from '../hooks/useResponsive';
import useAdmin from '../hooks/useAdmin';
import { useDataService } from '../context/DataContext';
import { formatDate } from '../utils/dateUtils';
import AdminLogin from '../components/admin/AdminLogin';
import DynamicAdminPanel from '../components/admin/DynamicAdminPanel';

/**
 * ULTIMATE VINTAGE HOME PAGE - FULLY INTEGRATED
 * 
 * Design Philosophy: Vintage typewriter + Original comprehensive sections
 * - Real database integration through DataService
 * - Dynamic admin panel with full CRUD
 * - Fixed header and navigation issues
 * - Professional footer with social links
 * - Mobile-responsive design
 */

const UltimateVintageHome = ({ onAdminClick }) => {
  const [posts, setPosts] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [profileData, setProfileData] = useState(null);
  const [careerData, setCareerData] = useState([]);
  const [educationData, setEducationData] = useState([]);
  const [projectsData, setProjectsData] = useState([]);
  const [awardsData, setAwardsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showRecentEssays, setShowRecentEssays] = useState(false); // Hidden by default
  
  const { isMobile, isTablet } = useResponsive();
  const { isAdmin, loginAdmin, logoutAdmin } = useAdmin();
  const dataService = useDataService();

  useEffect(() => {
    loadAllData();
  }, [dataService]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      
      // Load core data from API/mock
      const [postsData, profile] = await Promise.all([
        dataService.getPosts({ limit: 2 }), // Fetch only 2 posts for home page
        dataService.getProfile()
      ]);
      
      const allPosts = postsData.posts || postsData;
      setPosts(allPosts);
      setProfileData(profile);
      
      // Get top 2 most recent posts (sorted by created_at, latest first)
      const recentPosts = [...allPosts].sort((a, b) => {
        const dateA = new Date(a.created_at || a.published_at || 0);
        const dateB = new Date(b.created_at || b.published_at || 0);
        return dateB - dateA; // Latest first
      }).slice(0, 2);
      setFeaturedPosts(recentPosts); // Set only top 2 recent essays for home page
      
      // Load additional sections (fallback to mock if API not ready)
      try {
        const careerResponse = await dataService.getCareerExperience();
        const careerList = Array.isArray(careerResponse) 
          ? careerResponse 
          : (careerResponse.experiences || []);
        // Sort by order field (ascending - lower order numbers first) and get top 2
        const sortedCareer = [...careerList].sort((a, b) => {
          const orderA = a.order ?? 999;
          const orderB = b.order ?? 999;
          return orderA - orderB;
        }).slice(0, 2);
        setCareerData(sortedCareer);
      } catch (err) {
        const { careerExperience } = await import('../data/mock');
        const sortedCareer = [...careerExperience].sort((a, b) => {
          const orderA = a.order ?? 999;
          const orderB = b.order ?? 999;
          return orderA - orderB;
        }).slice(0, 2);
        setCareerData(sortedCareer);
      }
      
      try {
        const educationResponse = await dataService.getEducation();
        setEducationData(educationResponse);
      } catch (err) {
        const { education } = await import('../data/mock');
        setEducationData(education);
      }
      
      try {
        const projectsResponse = await dataService.getProjects();
        // Handle both array and object with projects property
        const projectsList = Array.isArray(projectsResponse) 
          ? projectsResponse 
          : (projectsResponse.projects || []);
        // Sort by order field (ascending - lower order numbers first)
        const sortedProjects = [...projectsList].sort((a, b) => {
          const orderA = a.order ?? 999; // Default to high number if order is missing
          const orderB = b.order ?? 999;
          return orderA - orderB;
        });
        setProjectsData(sortedProjects);
      } catch (err) {
        const { projects } = await import('../data/mock');
        setProjectsData(projects);
      }
      
      try {
        const awardsResponse = await dataService.getAwards();
        setAwardsData(awardsResponse);
      } catch (err) {
        const { awards } = await import('../data/mock');
        setAwardsData(awards);
      }
      
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminAccess = () => {
    if (isAdmin) {
      setShowAdminPanel(true);
    } else {
      setShowAdminLogin(true);
    }
  };

  const handleAdminLogin = (success) => {
    if (success) {
      loginAdmin(true);
      setShowAdminLogin(false);
      setShowAdminPanel(true);
    }
  };

  const handleAdminLogout = () => {
    logoutAdmin();
    setShowAdminPanel(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-vintage-cream flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vintage-accent mx-auto mb-4"></div>
          <p className="typewriter text-vintage-faded">Loading portfolio...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-vintage-cream">
        
        {/* HERO SECTION - Enhanced with Real Profile Data */}
        <section className="vintage-content-spacing pb-16">
          <div className="max-w-6xl mx-auto px-6">
            
            <div className="text-center mb-16">
              
              {/* Profile Image - Enhanced Design */}
              <div className="mb-8 flex justify-center">
                <div className="relative group">
                  <div className={`${isMobile ? 'w-32 h-32' : isTablet ? 'w-40 h-40' : 'w-48 h-48'} bg-vintage-ink rounded-full p-1 shadow-xl transition-all group-hover:shadow-2xl`}>
                    <div className={`w-full h-full bg-vintage-cream rounded-full flex items-center justify-center relative overflow-hidden border-2 border-vintage-accent`}>
                      {profileData?.avatar_url ? (
                        <img 
                          src={profileData.avatar_url} 
                          alt={profileData.name}
                          className="w-full h-full object-cover rounded-full"
                        />
                      ) : (
                        <span className={`vintage-heading-1 text-vintage-ink font-bold ${isMobile ? 'text-3xl' : isTablet ? 'text-4xl' : 'text-6xl'}`}>
                          {profileData?.name?.split(' ').map(n => n[0]).join('') || 'JV'}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Status Indicator */}
                  <div className={`absolute -bottom-2 -right-2 ${isMobile ? 'w-6 h-6' : 'w-8 h-8'} bg-vintage-accent border-4 border-vintage-cream rounded-full shadow-lg flex items-center justify-center`}>
                    <div className={`${isMobile ? 'w-2 h-2' : 'w-3 h-3'} bg-vintage-cream rounded-full animate-pulse`}></div>
                  </div>
                </div>
              </div>

              {/* Name and Title */}
              <div className="mb-8">
                <h1 className={`vintage-heading-1 ${isMobile ? 'text-4xl' : isTablet ? 'text-5xl' : 'text-6xl'} text-vintage-ink mb-4 leading-tight`}>
                  {profileData?.name || 'Jaswanth Vasudeva'}
                </h1>
                <p className={`${isMobile ? 'text-lg' : isTablet ? 'text-xl' : 'text-2xl'} text-vintage-faded typewriter font-light mb-4`}>
                  {profileData?.title || 'VC/PE Fellow & Strategic Leader'}
                </p>
                
                {/* Location */}
                {profileData?.location && (
                  <div className="flex items-center justify-center space-x-2 text-vintage-faded mb-6">
                    <MapPin size={16} />
                    <span className="vintage-caption">{profileData.location}</span>
                  </div>
                )}
              </div>

              {/* Tagline - Academic Quote Style */}
              <div className="mb-12">
                <div className="vintage-quote max-w-4xl mx-auto">
                  {profileData?.tagline || 'Building the future of venture capital and strategic growth initiatives'}
                </div>
              </div>

              {/* CTA Section */}
              <div className={`flex ${isMobile ? 'flex-col space-y-4' : 'flex-row'} items-center justify-center ${!isMobile ? 'space-x-6' : ''}`}>
                <Link 
                  to="/about"
                  className="vintage-btn vintage-btn-accent inline-flex items-center space-x-2"
                >
                  <span>Learn About My Work</span>
                  <ArrowRight size={16} />
                </Link>
                
                <Link 
                  to="/contact"
                  className="vintage-btn inline-flex items-center space-x-2"
                >
                  <span>Get In Touch</span>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* WRITING SECTION */}
        <section className="py-16 bg-vintage-paper">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="vintage-heading-3 text-vintage-ink mb-4">
                Writing Portfolio
              </h2>
              <p className="vintage-body text-vintage-faded max-w-3xl mx-auto">
                Award-winning writer with National Best Writer Award recognition. Focused on venture capital insights and strategic business commentary.
              </p>
            </div>

            {/* Writing Stats */}
            <div className={`grid ${isMobile ? 'grid-cols-1 gap-6' : 'grid-cols-1 md:grid-cols-3 gap-8'} mb-12`}>
              <div className="vintage-card p-6 text-center">
                <div className={`${isMobile ? 'text-3xl' : 'text-4xl'} font-bold text-vintage-accent mb-2 typewriter`}>50K+</div>
                <div className="vintage-caption">Total Views</div>
              </div>
              <div className="vintage-card p-6 text-center">
                <div className={`${isMobile ? 'text-3xl' : 'text-4xl'} font-bold text-vintage-accent mb-2 typewriter`}>2K+</div>
                <div className="vintage-caption">Followers</div>
              </div>
              <div className="vintage-card p-6 text-center">
                <div className={`${isMobile ? 'text-3xl' : 'text-4xl'} font-bold text-vintage-accent mb-2 typewriter`}>Award</div>
                <div className="vintage-caption">National Recognition</div>
              </div>
            </div>

            {/* Featured Achievement */}
            <div className="vintage-card p-8 text-center">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <Award className="text-vintage-accent" size={24} />
                <span className="vintage-heading-2 text-lg text-vintage-ink">Featured Achievement</span>
              </div>
              <h3 className="vintage-heading-1 text-2xl text-vintage-ink mb-2">National Best Writer Award</h3>
              <p className="vintage-body text-vintage-faded mb-4">Recognized by the Indian Film House for exceptional writing talent and contribution to literary arts.</p>
            </div>
          </div>
        </section>

        {/* CAREER SECTION */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="vintage-heading-3 text-vintage-ink mb-4">
                Professional Experience
              </h2>
              <p className="vintage-body text-vintage-faded max-w-3xl mx-auto">
                Strategic leadership roles across venture capital, business development, and software engineering
              </p>
            </div>

            <div className="space-y-8">
              {careerData.map((job) => (
                <div key={job.id} className="vintage-card p-8">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="w-12 h-12 bg-vintage-accent rounded-lg flex items-center justify-center flex-shrink-0">
                        <Briefcase className="text-vintage-cream" size={20} />
                      </div>
                      <div className="flex-1">
                        <h3 className="vintage-heading-2 text-xl text-vintage-ink mb-1">{job.role}</h3>
                        <p className="typewriter text-vintage-accent font-medium">{job.company}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-start md:items-end gap-2 mt-2 md:mt-0 md:ml-4">
                      <span className="vintage-caption text-vintage-faded">
                        {job.period}
                      </span>
                      {job.location && (
                        <span className="vintage-caption text-vintage-faded flex items-center space-x-1">
                          <MapPin size={14} />
                          <span>{job.location}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <p className="vintage-body text-vintage-faded mb-4">{job.description}</p>
                  
                  <div className="space-y-2">
                    {job.achievements.map((achievement, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <span className="text-vintage-accent typewriter mt-1">•</span>
                        <p className="vintage-body text-vintage-ink">{achievement}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EDUCATION SECTION */}
        <section className="py-16 bg-vintage-paper">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="vintage-heading-3 text-vintage-ink mb-4">
                Education
              </h2>
            </div>

            <div className="space-y-8">
              {educationData.filter(edu => edu.featured !== false).map((edu) => (
                <div key={edu.id} className="vintage-card p-8">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="w-12 h-12 bg-vintage-accent rounded-lg flex items-center justify-center flex-shrink-0">
                        <GraduationCap className="text-vintage-cream" size={20} />
                      </div>
                      <div className="flex-1">
                        <h3 className="vintage-heading-2 text-xl text-vintage-ink mb-1">{edu.degree}</h3>
                        <p className="typewriter text-vintage-accent font-medium">{edu.institution}</p>
                        {edu.grade && (
                          <p className="vintage-caption text-vintage-faded mt-1">{edu.grade}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-start md:items-end gap-2 mt-2 md:mt-0 md:ml-4">
                      <span className="vintage-caption text-vintage-faded">
                        {edu.period}
                      </span>
                      {edu.location && (
                        <span className="vintage-caption text-vintage-faded flex items-center space-x-1">
                          <MapPin size={14} />
                          <span>{edu.location}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <p className="vintage-body text-vintage-faded mb-4">{edu.description}</p>
                  
                  <div className="space-y-2">
                    {edu.achievements.map((achievement, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <span className="text-vintage-accent typewriter mt-1">•</span>
                        <p className="vintage-body text-vintage-ink">{achievement}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PROJECTS SECTION */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="vintage-heading-3 text-vintage-ink mb-4">
                Initiatives
              </h2>
              <p className="vintage-body text-vintage-faded max-w-3xl mx-auto">
                Impactful initiatives spanning social initiatives and technical implementations
              </p>
            </div>

            <div className={`grid ${isMobile ? 'grid-cols-1 gap-6' : 'grid-cols-1 md:grid-cols-2 gap-8'}`}>
              {projectsData.filter(project => project.featured).slice(0, 2).map((project) => {
                const getProjectUrl = (project) => {
                  if (project.links?.demo) return project.links.demo;
                  if (project.links?.article) return project.links.article;
                  if (project.links?.github) return project.links.github;
                  return null;
                };
                
                const projectUrl = getProjectUrl(project);
                
                return (
                  <article key={project.id} className="vintage-card p-8 hover:shadow-lg transition-shadow">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                      <h3 className="vintage-heading-2 text-2xl text-vintage-ink mb-3 md:mb-0 flex-1">
                        {project.title}
                      </h3>
                      {project.period && (
                        <div className="flex items-center space-x-1 text-sm text-vintage-faded md:ml-4">
                          <Calendar size={14} />
                          <span>{project.period}</span>
                        </div>
                      )}
                    </div>
                    
                    <p className="vintage-body text-vintage-faded leading-relaxed mb-4">
                      {project.description}
                    </p>
                    
                    {project.tags && project.tags.length > 0 && (
                      <div className="flex items-center flex-wrap gap-2 mb-4">
                        {project.tags.slice(0, 5).map((tag) => (
                          <span key={tag} className="vintage-caption text-vintage-faded flex items-center">
                            <Tag size={12} className="mr-1" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between pt-4 border-t" style={{ borderTopColor: 'var(--vintage-accent)' }}>
                      <div className="flex items-center space-x-2">
                        {project.featured && (
                          <span className="vintage-badge bg-vintage-accent text-vintage-cream">
                            Featured
                          </span>
                        )}
                        {project.category && (
                          <span className="vintage-badge">
                            {project.category}
                          </span>
                        )}
                      </div>
                      {projectUrl && (
                        <a 
                          href={projectUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-vintage-accent hover:text-vintage-ink font-medium transition-colors flex items-center space-x-1"
                        >
                          <span>View</span>
                          <ArrowRight size={14} />
                        </a>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="text-center mt-8">
              <Link 
                to="/initiatives"
                className="vintage-btn inline-flex items-center space-x-2"
              >
                <span>View All Initiatives</span>
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>

        {/* FEATURED POSTS SECTION */}
        {featuredPosts && featuredPosts.length > 0 && (
          <section className="py-16 bg-vintage-paper">
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center mb-12">
                <h2 className="vintage-heading-3 text-vintage-ink mb-4">
                  Featured Essays
                </h2>
                <p className="vintage-body text-vintage-faded max-w-3xl mx-auto">
                  Deep-dive analysis on venture capital, strategic business development, and emerging market trends
                </p>
              </div>
              
              <div className="space-y-8">
                {featuredPosts.slice(0, 2).map((post) => (
                  <article key={post.id} className="vintage-card p-8">
                    {/* Post Date */}
                    <div className="vintage-caption text-vintage-faded mb-2">
                      {formatDate(post.published_at || post.publishedAt)}
                    </div>

                    {/* Post Title */}
                    <h3 className="vintage-heading-1 text-2xl md:text-3xl text-vintage-ink mb-4 hover:text-vintage-accent transition-colors">
                      <Link to={`/post/${post.slug}`} className="no-underline">
                        {post.title}
                      </Link>
                    </h3>

                    {/* Post Excerpt */}
                    <p className="vintage-body text-vintage-faded mb-6 leading-relaxed">
                      {post.excerpt}
                    </p>

                    {/* Post Meta */}
                    <div className="flex flex-wrap items-center gap-4">
                      <span className="vintage-badge">
                        {post.category}
                      </span>
                      <span className="vintage-caption">
                        {post.read_time || post.readTime || 'N/A'}
                      </span>
                      <Link 
                        to={`/post/${post.slug}`}
                        className="text-vintage-accent hover:text-vintage-ink flex items-center space-x-1 no-underline transition-colors"
                      >
                        <span>Read Essay</span>
                        <ArrowRight size={14} />
                      </Link>
                    </div>
                  </article>
                ))}
              </div>

              <div className="text-center mt-8">
                <Link 
                  to="/blog"
                  className="vintage-btn inline-flex items-center space-x-2"
                >
                  <span>View All Essays</span>
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* RECENT POSTS INDEX */}
        {showRecentEssays && (
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="vintage-heading-3 text-vintage-ink mb-4">
                Recent Essays
              </h2>
            </div>
            
            <div className="space-y-4 max-w-4xl mx-auto">
              {posts.slice(0, 5).map((post) => (
                <div key={post.id} className="border-b border-vintage-muted pb-4">
                  <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'flex-row justify-between items-start'}`}>
                    <div className="flex-1">
                      <Link 
                        to={`/post/${post.slug}`}
                        className="vintage-link vintage-heading-2 text-lg text-vintage-ink hover:text-vintage-accent"
                      >
                        {post.title}
                      </Link>
                      <p className="vintage-caption text-vintage-faded mt-1">
                        {post.excerpt.substring(0, 120)}...
                      </p>
                    </div>
                    <div className={`typewriter text-vintage-faded text-sm ${isMobile ? 'self-start' : 'ml-4'}`}>
                      {formatDate(post.published_at || post.publishedAt)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        )}

      </div>

      {/* Admin Modals */}
      {showAdminLogin && (
        <AdminLogin 
          onLogin={handleAdminLogin}
          onClose={() => setShowAdminLogin(false)}
        />
      )}

      {showAdminPanel && (
        <DynamicAdminPanel 
          onClose={() => setShowAdminPanel(false)}
          onLogout={handleAdminLogout}
        />
      )}

      {/* Hidden Admin Access Trigger */}
      {!isAdmin && (
        <div 
          className="fixed bottom-4 left-4 w-4 h-4 opacity-0 cursor-pointer"
          onDoubleClick={() => setShowAdminLogin(true)}
          title="Admin Access (Double Click)"
        />
      )}
    </>
  );
};

export default UltimateVintageHome;